import type { ReactNode } from 'react';
import React, { createContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const UserLoginType = {
  LOGIN: 'login',
  SIGNUP: 'signup',
} as const;

export const UserLoginSource = {
  GOOGLE: 'google',
  EMAIL: 'email',
  FACEBOOK: 'meta',
} as const;

export const UserLoginOrigin = {
  PROMPT: 'payerprompt',
  TRACKING: 'tracking',
  MENU: 'menu',
} as const;

export type UserLoginOriginType =
  (typeof UserLoginOrigin)[keyof typeof UserLoginOrigin];

export interface LoginContextInterface {
  loginOrigin: string;
  setLoginOrigin: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const LoginContext = createContext<LoginContextInterface>({
  loginOrigin: UserLoginOrigin.MENU,
  setLoginOrigin: () => {},
});

interface LoginContextProviderProps {
  children: ReactNode;
  loginOrigin?: string;
}

function LoginContextProvider({
  children,
  loginOrigin: initialOrigin,
}: LoginContextProviderProps) {
  const location = useLocation();
  const [loginOrigin, setLoginOrigin] = useState<string | undefined>(
    initialOrigin,
  );

  const value: LoginContextInterface = {
    loginOrigin: loginOrigin || UserLoginOrigin.MENU,
    setLoginOrigin,
  };

  useEffect(() => {
    if (!loginOrigin) {
      const pathLocation = location.pathname;
      const origin = pathLocation.includes('/tracking')
        ? UserLoginOrigin.TRACKING
        : UserLoginOrigin.MENU;
      setLoginOrigin(origin);
    }
  }, [location.pathname, loginOrigin]);

  return (
    <LoginContext.Provider value={value}>{children}</LoginContext.Provider>
  );
}

export { LoginContext, LoginContextProvider };
